import { ReactNode, useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSwipeNavigation } from '@/hooks/useSwipeNavigation';
import { useIsMobile } from '@/hooks/use-mobile';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import { useHaptic, HAPTIC_PATTERNS } from '@/hooks/useHaptic';
import { getNextPage, getPreviousPage } from '@/lib/navigationOrder';
import { supabase } from '@/integrations/supabase/client';
import { SwipeTutorialOverlay } from './SwipeTutorialOverlay';

interface SwipeableLayoutProps {
  children: ReactNode;
}

export const SwipeableLayout = ({ children }: SwipeableLayoutProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const prefersReducedMotion = useReducedMotion();
  const { triggerHaptic, isHapticAvailable } = useHaptic();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [nextPagePath, setNextPagePath] = useState<string | null>(null);
  const [prevPagePath, setPrevPagePath] = useState<string | null>(null);
  const boundaryHapticTriggeredRef = useRef(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const tutorialShownRef = useRef(false);

  useEffect(() => {
    const checkAdmin = async () => {
      // SECURITY NOTE: This client-side check is for UX only (determining navigation flow).
      // The actual security is enforced server-side via RLS policies and the AdminDashboard
      // route guard that redirects non-admins. An attacker could manipulate this state to
      // access admin routes, but they will be redirected and cannot access admin data.
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data } = await supabase.rpc('has_role', {
          _user_id: user.id,
          _role: 'admin'
        });
        
        setIsAdmin(!!data);
      }
    };

    checkAdmin();
  }, []);

  useEffect(() => {
    setNextPagePath(getNextPage(location.pathname, isAdmin));
    setPrevPagePath(getPreviousPage(location.pathname, isAdmin));
  }, [location.pathname, isAdmin]);

  useEffect(() => {
    // Only check on mobile
    if (!isMobile) return;
    
    // Check if tutorial was already shown
    const tutorialShown = localStorage.getItem('swipe_tutorial_shown');
    
    if (!tutorialShown && !tutorialShownRef.current) {
      // Small delay to allow page to settle
      const timer = setTimeout(() => {
        setShowTutorial(true);
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [isMobile]);

  const handleDismissTutorial = useCallback(() => {
    localStorage.setItem('swipe_tutorial_shown', 'true');
    tutorialShownRef.current = true;
    setShowTutorial(false);
  }, []);

  const shouldTriggerHaptic = useCallback(() => {
    if (prefersReducedMotion) return false;
    if (!isHapticAvailable()) return false;
    return true;
  }, [prefersReducedMotion, isHapticAvailable]);

  const handleDragStart = () => {
    setIsDragging(true);
    boundaryHapticTriggeredRef.current = false;
  };

  const handleDragMove = (deltaX: number, progress: number) => {
    const SNAP_THRESHOLD = 0.3;
    
    // Add rubber-band resistance at edges
    if ((deltaX > 0 && !prevPagePath) || (deltaX < 0 && !nextPagePath)) {
      // Trigger boundary haptic once per gesture
      if (!boundaryHapticTriggeredRef.current && Math.abs(deltaX) > 20) {
        if (shouldTriggerHaptic()) {
          triggerHaptic(HAPTIC_PATTERNS.BOUNDARY);
        }
        boundaryHapticTriggeredRef.current = true;
      }
      setDragOffset(deltaX * 0.2); // Reduced movement at boundaries
      return;
    }
    
    // Reset flag when moving away from boundary
    if (boundaryHapticTriggeredRef.current && Math.abs(deltaX) < 10) {
      boundaryHapticTriggeredRef.current = false;
    }
    
    setDragOffset(deltaX);
  };

  const handleDragEnd = () => {
    const SNAP_THRESHOLD = 0.3;
    const screenWidth = window.innerWidth;
    const progress = Math.abs(dragOffset) / screenWidth;

    if (progress > SNAP_THRESHOLD) {
      // Complete the swipe
      if (dragOffset > 0 && prevPagePath) {
        if (shouldTriggerHaptic()) {
          triggerHaptic(HAPTIC_PATTERNS.SWIPE_SUCCESS);
        }
        setIsTransitioning(true);
        navigate(prevPagePath);
        setTimeout(() => {
          setIsTransitioning(false);
          setDragOffset(0);
          setIsDragging(false);
        }, prefersReducedMotion ? 0 : 300);
      } else if (dragOffset < 0 && nextPagePath) {
        if (shouldTriggerHaptic()) {
          triggerHaptic(HAPTIC_PATTERNS.SWIPE_SUCCESS);
        }
        setIsTransitioning(true);
        navigate(nextPagePath);
        setTimeout(() => {
          setIsTransitioning(false);
          setDragOffset(0);
          setIsDragging(false);
        }, prefersReducedMotion ? 0 : 300);
      } else {
        // Snap back
        setDragOffset(0);
        setIsDragging(false);
      }
    } else {
      // Snap back
      setDragOffset(0);
      setIsDragging(false);
    }
  };

  const handleSwipeLeft = () => {
    if (isTransitioning || isDragging) return;
    
    if (showTutorial) {
      handleDismissTutorial();
    }
    
    const nextPage = getNextPage(location.pathname, isAdmin);
    if (nextPage) {
      if (shouldTriggerHaptic()) {
        triggerHaptic(HAPTIC_PATTERNS.SWIPE_SUCCESS);
      }
      setIsTransitioning(true);
      navigate(nextPage);
      setTimeout(() => setIsTransitioning(false), prefersReducedMotion ? 0 : 300);
    }
  };

  const handleSwipeRight = () => {
    if (isTransitioning || isDragging) return;
    
    if (showTutorial) {
      handleDismissTutorial();
    }
    
    const previousPage = getPreviousPage(location.pathname, isAdmin);
    if (previousPage) {
      if (shouldTriggerHaptic()) {
        triggerHaptic(HAPTIC_PATTERNS.SWIPE_SUCCESS);
      }
      setIsTransitioning(true);
      navigate(previousPage);
      setTimeout(() => setIsTransitioning(false), prefersReducedMotion ? 0 : 300);
    }
  };

  const containerRef = useSwipeNavigation(
    {
      onSwipeLeft: handleSwipeLeft,
      onSwipeRight: handleSwipeRight,
      onDragStart: handleDragStart,
      onDragMove: handleDragMove,
      onDragEnd: handleDragEnd,
    },
    isMobile
  );

  const progress = Math.abs(dragOffset) / window.innerWidth;
  const scale = 1 - progress * 0.05;
  const opacity = 1 - progress * 0.3;

  return (
    <div
      ref={containerRef}
      className="min-h-screen relative overflow-hidden"
      style={{
        touchAction: 'pan-y pinch-zoom',
      }}
    >
      {/* Tutorial Overlay */}
      {showTutorial && (
        <SwipeTutorialOverlay
          isVisible={showTutorial}
          onDismiss={handleDismissTutorial}
          canSwipeLeft={!!nextPagePath}
          canSwipeRight={!!prevPagePath}
        />
      )}

      {/* Current Page */}
      <div
        className={`min-h-screen ${
          !isDragging && (isTransitioning || dragOffset !== 0) && !prefersReducedMotion
            ? 'transition-all duration-300 ease-out'
            : ''
        }`}
        style={{
          transform: prefersReducedMotion 
            ? 'none' 
            : `translateX(${dragOffset}px) scale(${isDragging ? scale : 1})`,
          opacity: isDragging ? opacity : 1,
          willChange: isDragging ? 'transform' : 'auto',
        }}
      >
        {children}
      </div>

      {/* Next Page Preview (Right) */}
      {nextPagePath && dragOffset < 0 && !prefersReducedMotion && (
        <div
          className="fixed inset-0 pointer-events-none z-0"
          style={{
            transform: `translateX(${100 + (dragOffset / window.innerWidth) * 100}%)`,
            opacity: progress,
            willChange: 'transform',
          }}
        >
          <div className="w-full h-full bg-background/80 backdrop-blur-sm flex items-center justify-center">
            <div className="text-muted-foreground">→</div>
          </div>
        </div>
      )}

      {/* Previous Page Preview (Left) */}
      {prevPagePath && dragOffset > 0 && !prefersReducedMotion && (
        <div
          className="fixed inset-0 pointer-events-none z-0"
          style={{
            transform: `translateX(${-100 + (dragOffset / window.innerWidth) * 100}%)`,
            opacity: progress,
            willChange: 'transform',
          }}
        >
          <div className="w-full h-full bg-background/80 backdrop-blur-sm flex items-center justify-center">
            <div className="text-muted-foreground">←</div>
          </div>
        </div>
      )}
    </div>
  );
};
